import { styled } from '@mui/system';
import { useViewport } from 'contexts/viewport';
import { isEqual } from 'lodash';
import { memo, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from 'shell/components/Button';
import Icon from 'shell/components/Icon';
import Stack from 'shell/components/Stack';
import Text from 'shell/components/Text';

const truncateToMaxLines = (htmlString, maxLines) => {
    const parsedDocument = new DOMParser().parseFromString(htmlString, 'text/html');

    const paragraphs = Array.from(parsedDocument.body.querySelectorAll('p'));
    if (paragraphs.length <= maxLines) {
        return htmlString;
    }

    const truncated = paragraphs
        .slice(0, maxLines - 1)
        .map((p) => p.outerHTML)
        .join('');

    const lastParagraph = paragraphs[maxLines - 1];
    const lastText = lastParagraph?.textContent.trim();

    // append ellipsis inline with the last visible paragraph
    return `${truncated}<p>${lastText} ...</p>`;
};

const ViewMore = memo(
    ({ children, maxLines = 4, TextComponentProps = {}, variant }) => {
        const { t } = useTranslation();

        const textRef = useRef(null);

        const [displayText, setDisplayText] = useState(children);
        const [isOverflowing, setIsOverflowing] = useState(false);
        const [isExpanded, setIsExpanded] = useState(false);

        const toggleExpand = (e) => {
            e.stopPropagation();
            setIsExpanded((prev) => !prev);
        };

        const { width } = useViewport();

        useLayoutEffect(() => {
            if (textRef.current) {
                const { clientHeight, scrollHeight } = textRef.current;
                if (!isExpanded) {
                    const truncatedText = truncateToMaxLines(children, maxLines);
                    setDisplayText(truncatedText);
                    setIsOverflowing(scrollHeight > clientHeight + 1);
                } else {
                    setDisplayText(children);
                }
            }
        }, [children, isExpanded, maxLines, width]);

        return (
            <Stack alignItems={'flex-start'} spacing={2}>
                <TextWrapper ref={textRef} $isExpanded={isExpanded} maxLines={maxLines} {...TextComponentProps}>
                    {displayText}
                </TextWrapper>
                {isOverflowing && (
                    <Button
                        color={variant ?? 'neutral'}
                        endIcon={
                            isExpanded ? (
                                <Icon kind={variant === 'primary' ? 'chevronUp' : 'remove'} />
                            ) : (
                                <Icon kind={variant === 'primary' ? 'chevronDown' : 'add'} />
                            )
                        }
                        variant={'borderless'}
                        onClick={toggleExpand}
                    >
                        {isExpanded ? t('controls.show-less', 'Show Less') : t('controls.show-more', 'Show More')}
                    </Button>
                )}
            </Stack>
        );
    },
    (prevProps, nextProps) => isEqual(prevProps, nextProps)
);

const TextWrapper = styled(Text)(({ $isExpanded, maxLines }) => ({
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    WebkitLineClamp: $isExpanded ? 'none' : maxLines,
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
}));

export default ViewMore;
